import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ArrowRight } from "@phosphor-icons/react";

/**
 * Hidden URL `/sa/login` — dedicated login for platform super admins.
 *
 * Design notes (28-Jul-2026):
 *   * Not linked from anywhere in the normal user flow.
 *   * Uses a distinct backend endpoint `/api/auth/super-login` that
 *     rejects users without `platform_role === "super_admin"` with the
 *     generic 401 "Invalid credentials", so this endpoint never reveals
 *     that a non-super-admin email exists.
 *   * Same workspace-selection contract as regular login — super admins
 *     with 2+ memberships must pick one; JWT is scoped to that org.
 */
export default function SuperAdminLoginPage() {
  const { superLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [workspacePick, setWorkspacePick] = useState(null);

  const attempt = async (pickedSlug) => {
    setLoading(true);
    try {
      const result = await superLogin(email, password, pickedSlug || null);
      if (result?.needs_workspace_selection) {
        setWorkspacePick(result.orgs || []);
        return;
      }
      setWorkspacePick(null);
      toast.success("Welcome, super admin");
      // Super-admin login ALWAYS lands on /platform, regardless of whether
      // this user also happens to hold workspace-level roles like owner or
      // admin. This keeps the super-admin flow visually and behaviourally
      // distinct from the regular /login flow.
      navigate("/platform");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    await attempt(null);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-slate-100 px-4" data-testid="super-admin-login-page">
      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center gap-2">
          <div className="h-12 w-12 rounded-lg bg-slate-800 border border-slate-700 flex items-center justify-center">
            <ShieldCheck size={22} weight="fill" className="text-emerald-400" />
          </div>
          <div className="text-xl font-semibold tracking-tight">Platform admin sign in</div>
          <p className="text-xs text-slate-400 text-center max-w-xs">
            Restricted access. This portal is for Emergent platform operators only.
          </p>
        </div>

        <Card className="bg-slate-900 border-slate-800">
          <CardContent className="p-6 space-y-4">
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="sa-email" className="text-slate-300">Email</Label>
                <Input
                  id="sa-email" type="email" required autoComplete="email"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@emergent.local"
                  className="bg-slate-950 border-slate-700 text-slate-100"
                  data-testid="sa-login-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sa-password" className="text-slate-300">Password</Label>
                <PasswordInput
                  id="sa-password" required autoComplete="current-password"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  inputClassName="bg-slate-950 border-slate-700 text-slate-100"
                  data-testid="sa-login-password"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full gap-2 bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="sa-login-submit">
                {loading ? "Signing in…" : "Sign in"}
                <ArrowRight size={16} />
              </Button>
            </form>

            {workspacePick && workspacePick.length > 0 && (
              <div className="pt-2 border-t border-slate-800 space-y-2" data-testid="sa-workspace-picker">
                <div className="text-sm text-slate-300 font-medium">Pick a workspace</div>
                <div className="grid gap-1.5">
                  {workspacePick.map(o => (
                    <button
                      key={o.slug} type="button"
                      onClick={() => attempt(o.slug)}
                      disabled={loading}
                      data-testid={`sa-pick-${o.slug}`}
                      className="flex items-center justify-between rounded-md border border-slate-700 bg-slate-950 px-3 py-2 hover:bg-slate-800 transition text-left"
                    >
                      <span>
                        <span className="font-medium text-sm text-slate-100">{o.name}</span>
                        <span className="block text-[10px] text-slate-500">/t/{o.slug}</span>
                      </span>
                      <ArrowRight size={14} className="text-slate-500" />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
